import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import classService from "../../services/classService";

// Async thunks
export const fetchClasses = createAsyncThunk(
  "classes/fetchClasses",
  async (params, { rejectWithValue }) => {
    try {
      const response = await classService.getAllClasses(params);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch classes"
      );
    }
  }
);

export const createClass = createAsyncThunk(
  "classes/createClass",
  async ({ classData, imageFile }, { rejectWithValue }) => {
    try {
      const response = await classService.createClass(classData, imageFile);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create class"
      );
    }
  }
);

export const updateClass = createAsyncThunk(
  "classes/updateClass",
  async ({ id, classData, imageFile }, { rejectWithValue }) => {
    try {
      const response = await classService.updateClass(id, classData, imageFile);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update class"
      );
    }
  }
);

export const deleteClass = createAsyncThunk(
  "classes/deleteClass",
  async (id, { rejectWithValue }) => {
    try {
      const response = await classService.deleteClass(id);
      return { id, response };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete class"
      );
    }
  }
);

const classSlice = createSlice({
  name: "classes",
  initialState: {
    classes: [],
    isLoading: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    error: null,
    success: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch classes
      .addCase(fetchClasses.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchClasses.fulfilled, (state, action) => {
        state.isLoading = false;
        const responseData = action.payload.data || action.payload;
        state.classes = responseData.classes || responseData || [];
      })
      .addCase(fetchClasses.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Create class
      .addCase(createClass.pending, (state) => {
        state.isCreating = true;
        state.error = null;
        state.success = null;
      })
      .addCase(createClass.fulfilled, (state, action) => {
        state.isCreating = false;
        const responseData = action.payload.data || action.payload;
        const newClass = responseData.class || responseData;
        if (newClass) {
          state.classes.unshift(newClass);
        }
        state.success = action.payload.message || "Class created successfully";
      })
      .addCase(createClass.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload;
      })
      // Update class
      .addCase(updateClass.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateClass.fulfilled, (state, action) => {
        state.isUpdating = false;
        const responseData = action.payload.data || action.payload;
        const updatedClass = responseData.class || responseData;
        const index = state.classes.findIndex((c) => c.id === updatedClass.id);
        if (index !== -1) {
          state.classes[index] = updatedClass;
        }
        state.success = action.payload.message || "Class updated successfully";
      })
      .addCase(updateClass.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload;
      })
      // Delete class
      .addCase(deleteClass.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
        state.success = null;
      })
      .addCase(deleteClass.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.classes = state.classes.filter((c) => c.id !== action.payload.id);
        state.success = action.payload.response.message || "Class deleted successfully";
      })
      .addCase(deleteClass.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearSuccess } = classSlice.actions;
export default classSlice.reducer;
